import type { ExtractRequestBody } from "core/entities/APITypes";
import { useCentrixFetchPublic } from "core/hooks/useCentrixFetch";
import { useGReCaptcha } from "core/hooks/useGReCaptcha";
import { uploadToAzureBlobStorage } from "core/utils/AzureBlobStorage";
import { getAzureUploadProgressEventHandler } from "core/utils/AzureBlobStorage/azureUpload";
import { normalizeExtension } from "core/utils/normalizeExtension";
import { useCallback } from "react";

export type EstimateRequestImageUploadErrorCode =
  | "RECAPTCHA_NOT_LOADED"
  | "RECAPTCHA_NOT_GENERATED"
  | "NO_FILE_SELECTED"
  | "FETCH_UPLOAD_TOKEN_FAILED"
  | "CONFIRM_UPLOAD_FAILED"
  | "BLOB_UPLOAD_FAILED";

type UploadSuccess = {
  ok: true;
  file: File;
  fileId: string;
  fileName: string;
};

type UploadFailure = {
  ok: false;
  errorCode: EstimateRequestImageUploadErrorCode;
  error?: any;
};

export function useEstimateRequestImageUpload() {
  const recaptcha = useGReCaptcha();
  const centrixFetch = useCentrixFetchPublic();
  return useCallback(
    async (
      file: File,
      body: Pick<
        ExtractRequestBody<"/api/app/estimate-request/upload-token">,
        "estimateRequestId" | "imageLocation" | "imageNumber" | "shopId"
      >,
      onLoadingProgress?: (progress: number | null) => void
    ): Promise<UploadFailure | UploadSuccess> => {
      if (recaptcha.isLoading) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "RECAPTCHA_NOT_LOADED" };
      }
      if (!file) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "NO_FILE_SELECTED" };
      }
      onLoadingProgress?.(1);
      const fileName = normalizeExtension(file.name);

      const recaptchaGenerateUploadTokenToken = await recaptcha
        .execute("public_estimate_request/generate_upload_token")
        .catch(() => null);
      if (!recaptchaGenerateUploadTokenToken) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "RECAPTCHA_NOT_GENERATED" };
      }
      const uploadTokenRes = await centrixFetch({
        method: "post",
        path: "/api/app/estimate-request/upload-token",
        body: {
          ...body,
          fileName,
          gRecaptchaResponse: recaptchaGenerateUploadTokenToken,
        },
      });
      if (!uploadTokenRes.ok) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "FETCH_UPLOAD_TOKEN_FAILED" };
      }
      onLoadingProgress?.(33);
      const tokenInfo = await uploadTokenRes.json();
      const res = await uploadToAzureBlobStorage(
        file,
        tokenInfo.accessToken,
        getAzureUploadProgressEventHandler(onLoadingProgress, 33, 66)
      );
      if (!res?.ok) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "BLOB_UPLOAD_FAILED" };
      }
      onLoadingProgress?.(66);

      const recaptchaGenerateConfirmUploadToken = await recaptcha
        .execute("public_estimate_request/confirm_upload")
        .catch(() => null);
      if (!recaptchaGenerateConfirmUploadToken) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "RECAPTCHA_NOT_GENERATED" };
      }
      const confirmationRes = await centrixFetch({
        method: "put",
        path: "/api/app/estimate-request/confirm-upload",
        body: {
          fileUploadValidationId: tokenInfo.fileUploadValidationId,
          uploadAccessToken: tokenInfo.accessToken,
          gRecaptchaResponse: recaptchaGenerateConfirmUploadToken,
        },
      });

      if (!confirmationRes.ok) {
        onLoadingProgress?.(null);
        return { ok: false, errorCode: "CONFIRM_UPLOAD_FAILED" };
      }
      onLoadingProgress?.(100);
      return {
        ok: true,
        file,
        fileName,
        fileId: tokenInfo.fileUploadValidationId,
      };
    },
    [centrixFetch, recaptcha]
  );
}
